/*
  Signature Legacy — Vault Deposits (FIXED)
  - Ajax add-to-cart
  - Enforce single deposit (optional) using /cart/update.js (key-safe)
  - Always refresh cart drawer UI after cart mutation (prevents "sometimes not visible" bugs)
*/

(() => {
  const roots = document.querySelectorAll('[data-sigvault]');
  if (!roots.length) return;

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(txt || `Request failed: ${res.status}`);
    }
    return res.json();
  };

  const postJSON = (url, body) =>
    fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

  const getCart = () => fetchJSON('/cart.js');

  /**
   * Cart UI refresh (drawer + bubble) — prevents:
   * - "added but not visible"
   * - "always shows the old (diamond) item"
   * - remove buttons missing valid parameters in stale DOM
   */
  const refreshCartUI = async () => {
    // Try common section ids (Dawn/Prestige 계열에서 자주 사용)
    const sectionIds = new Set();

    // cart drawer element itself
    const drawerEl = document.querySelector('cart-drawer');
    // bubble (depends on theme)
    const bubbleEl =
      document.querySelector('#cart-icon-bubble') ||
      document.querySelector('cart-icon-bubble') ||
      document.querySelector('[data-cart-count]') ||
      null;

    // 가장 흔한 섹션 id들
    sectionIds.add('cart-drawer');
    sectionIds.add('cart-icon-bubble');

    // 테마마다 섹션 id가 다를 수 있어, DOM에 존재하면 그 id도 추가
    if (drawerEl?.id) sectionIds.add(drawerEl.id);
    if (bubbleEl?.id) sectionIds.add(bubbleEl.id);

    const ids = Array.from(sectionIds).filter(Boolean);
    if (!ids.length) {
      // 마지막 보험: 이벤트라도 쏴줌
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    // Section Rendering API
    const url = `/cart?sections=${encodeURIComponent(ids.join(','))}`;
    const sections = await fetchJSON(url);

    // Dawn 계열: cart-drawer.renderContents({ sections })
    if (drawerEl && typeof drawerEl.renderContents === 'function') {
      drawerEl.renderContents({ sections });
    } else {
      // fallback: bubble만이라도 교체 시도
      if (bubbleEl && sections['cart-icon-bubble']) {
        try {
          bubbleEl.outerHTML = sections['cart-icon-bubble'];
        } catch (_) {}
      }
    }

    // Many themes listen to this event
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  const openCartDrawerBestEffort = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    const toggle =
      document.querySelector('[data-action="open-cart-drawer"]') ||
      document.querySelector('[href="/cart"]') ||
      document.querySelector('a[href*="cart"]');

    if (toggle && typeof toggle.click === 'function') {
      toggle.click();
      return true;
    }
    return false;
  };

  const setLoading = (btn, on) => {
    if (!btn) return;
    btn.disabled = !!on;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) {
      btn.dataset._label = btn.textContent;
      btn.textContent = 'ADDING...';
    } else {
      btn.textContent = btn.dataset._label || btn.textContent;
      delete btn.dataset._label;
    }
  };

  const normalizeVariantId = (v) => {
    const n = String(v || '').trim();
    return /^\d+$/.test(n) ? n : '';
  };

  roots.forEach((root) => {
    const afterAdd = root.dataset.afterAdd || 'drawer';
    const enforceSingle = (root.dataset.enforceSingle || 'false') === 'true';

    const grid = root.querySelector('.sigvault__grid');
    const tierVariantIds = (grid?.dataset.variantIds || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(String);

    let busy = false;

    const handleAdd = async (variantIdRaw) => {
      const variantId = normalizeVariantId(variantIdRaw);
      if (!variantId) return;

      // ✅ enforce single: remove other deposit tiers safely with /cart/update.js (key-supported)
      if (enforceSingle && tierVariantIds.length) {
        const cart = await getCart();

        // deposit 라인아이템만 골라서:
        // - 선택한 variant는 quantity=1로 고정
        // - 나머지는 quantity=0으로 제거
        const updates = {};
        let selectedExists = false;

        cart.items.forEach((it) => {
          const vid = String(it.variant_id);
          if (!tierVariantIds.includes(vid)) return;

          if (vid === String(variantId)) {
            selectedExists = true;
            // 이미 카트에 있으면 1로 고정 (중복 클릭해도 2,3으로 안 올라가게)
            updates[it.key] = 1;
          } else {
            updates[it.key] = 0;
          }
        });

        if (Object.keys(updates).length) {
          await postJSON('/cart/update.js', { updates });
        }

        // 선택한 variant가 카트에 없었다면 add 수행
        if (!selectedExists) {
          await postJSON('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
        }
      } else {
        // ✅ enforce off: normal add (multiple deposits allowed)
        await postJSON('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
      }

      // ✅ Always refresh drawer UI so items reliably appear
      await refreshCartUI();
    };

    root.addEventListener('click', async (e) => {
      // Button add
      const btn = e.target.closest('[data-sigvault-add]');
      // Optional: card click add
      const card = e.target.closest('[data-sigvault-card][role="button"]');

      // 버튼이 있으면 버튼 우선, 없으면 카드 클릭(설정 ON일 때) 허용
      const variantId =
        normalizeVariantId(btn?.dataset?.variantId) ||
        normalizeVariantId(card?.dataset?.variantId);

      if (!variantId) return;

      // 중복 클릭 방지
      if (busy) return;
      busy = true;

      const loadingBtn = btn || card?.querySelector('[data-sigvault-add]');

      try {
        setLoading(loadingBtn, true);
        await handleAdd(variantId);

        if (afterAdd === 'checkout') return (window.location.href = '/checkout');
        if (afterAdd === 'cart') return (window.location.href = '/cart');

        const opened = openCartDrawerBestEffort();
        if (!opened) window.location.href = '/cart';
      } catch (err) {
        console.error(err);
        alert('장바구니 담기에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      } finally {
        setLoading(loadingBtn, false);
        busy = false;
      }
    });

    root.addEventListener('keydown', async (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;

      const card = e.target.closest('[data-sigvault-card][role="button"]');
      if (!card) return;

      e.preventDefault();

      const variantId = normalizeVariantId(card.dataset.variantId);
      if (!variantId) return;

      if (busy) return;
      busy = true;

      const btn = card.querySelector('[data-sigvault-add]');

      try {
        setLoading(btn, true);
        await handleAdd(variantId);

        if (afterAdd === 'checkout') return (window.location.href = '/checkout');
        if (afterAdd === 'cart') return (window.location.href = '/cart');

        const opened = openCartDrawerBestEffort();
        if (!opened) window.location.href = '/cart';
      } catch (err) {
        console.error(err);
        alert('장바구니 담기에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      } finally {
        setLoading(btn, false);
        busy = false;
      }
    });
  });
})();
